import {
  BILLING_MODULE_KEYS,
  type BillingModuleKey,
  type ModuleKey,
} from '@bill-alps/backend/features/companies/modules';
import type { Plan } from '@bill-alps/backend/shared/billing/planLimits';
import {
  isBillingModuleKey,
  normalizeModuleSet,
  type BillingModuleSet,
  type LegacyModuleSelection,
} from '@bill-alps/backend/shared/billing/plans';

/**
 * What a company's subscription actually pays for, and what it does not.
 *
 * `companies.billedModules` is billing state, not entitlement: it records the
 * module set of the Stripe package (see `BILLING_PACKAGES`). The module flags
 * (`invoicingEnabled` & co.) answer the *other* question — whether a module is
 * switched on at all — and a package must never switch a module off: every
 * module ships on the free plan, so paying for payroll can never take away
 * invoicing or accounting.
 *
 * Absent `billedModules` means invoicing + payroll, i.e. the behaviour every
 * paid company had before the field existed. That is what makes the code
 * deploy-safe ahead of the backfill: a paid company without a value never
 * gains or loses accounting by surprise.
 *
 * Browser-safe on purpose (no `process` access), so the app imports this
 * module directly instead of keeping a second copy of the rule.
 */

export const BILLED_MODULE_KEYS: readonly BillingModuleKey[] =
  BILLING_MODULE_KEYS;

/** Stored value: the legacy single string, or the current array. */
export type StoredBilledModules = LegacyModuleSelection | BillingModuleSet;

/** What an absent value means — the pre-accounting package shape. */
export const DEFAULT_BILLED_MODULES: BillingModuleSet = [
  'invoicing',
  'payroll',
];

export { isBillingModuleKey };

/**
 * Normalises any stored form to a canonical array. Never throws — this runs
 * inside queries — so an empty or unreadable value falls back to the default.
 */
export function normalizeBilledModules(
  value: StoredBilledModules | null | undefined,
): BillingModuleKey[] {
  if (value === undefined || value === null) return [...DEFAULT_BILLED_MODULES];
  if (typeof value === 'string') {
    if (value === 'both') return [...DEFAULT_BILLED_MODULES];
    return isBillingModuleKey(value) ? [value] : [...DEFAULT_BILLED_MODULES];
  }
  const keys = value.filter(isBillingModuleKey);
  return keys.length > 0
    ? normalizeModuleSet(keys)
    : [...DEFAULT_BILLED_MODULES];
}

/** Whether a package's module set includes a given billed module. */
export function billedModulesCover(
  billedModules: BillingModuleSet,
  module: BillingModuleKey,
): boolean {
  return billedModules.includes(module);
}

/** The stored package shape, defaulting to the pre-field behaviour. */
export function getBilledModules(
  company: { billedModules?: StoredBilledModules } | null | undefined,
): BillingModuleKey[] {
  return normalizeBilledModules(company?.billedModules);
}

/**
 * The plan a module runs on. A paid company keeps its plan for the modules its
 * package covers and falls back to `free` limits for the others.
 *
 * Every module-scoped gate goes through here, never through `company.plan`
 * directly.
 *
 * Deliberately mirrors the `company.plan ?? 'free'` idiom rather than
 * `getEffectivePlan`: the sandbox forces `plan: 'business'` into the row
 * itself (see `sandboxImport/transform.ts`), so reading the column is already
 * the sandbox-correct answer.
 *
 * Left on the plain company plan on purpose:
 *   - team invitations and seat suspension (`companyMembers/*`) — seats are
 *     company-level, not per module;
 *   - the business-plan API-key gate (`apiKeys/rules.ts`) — a key is
 *     company-level too.
 */
export function getModulePlan(
  company:
    { plan?: Plan; billedModules?: StoredBilledModules } | null | undefined,
  module: ModuleKey,
): Plan {
  const plan = company?.plan ?? 'free';
  if (plan === 'free') return 'free';
  return billedModulesCover(getBilledModules(company), module) ? plan : 'free';
}
